<template>
  <VSpinner v-if="isLoading" class="offset-top-2" />
  <article v-else id="CONTRACT_SEARCH" class="container-fixed offset-bottom-4">
    <header>
      <div v-if="errorSelectContract" class="offset-top-2">
        <p />
        <div class="notification notification-negative" role="alert">
          <h3 class="notification-heading">{{ $t('common.headerError') }}</h3>
          <div class="notification-content">
            {{ $t('common.errorSelectContract') }}
          </div>
        </div>
      </div>
    </header>

    <VSection v-if="isContractSelectionVisible" id="CONTRACT_SELECTION" label="common.headerOverview" h2 :collapsed.sync="isCollapsed">
      <transition name="slide-fade" mode="out-in" appear>
        <VMessage v-if="msgBox.type" :key="msgBox.type" v-bind="msgBox">
          <template #error="{ message }">
            <p v-html="message" />
            <VButton class="btn-default offset-top-1" label="common.contract_search_selection_retry_button_label" @click.prevent="onLoadList" />
          </template>
        </VMessage>
        <VContractSelection v-else :contracts="contracts" @select="onContractSearch" />
      </transition>
    </VSection>

    <section>
      <VTabset label="common.contractSearchHeader" @select:tab="onTabChange">
        <div class="row">
          <div class="col-l-5 col-m-12">
            <VTab v-if="isSearchByContractIdentifierAllowed" label="common.contract" name="formContractIdentifier">
              <VForm v-slot="{ isInit }" :form="formContractIdentifier" @submit="onContractSearch">
                <VInput name="identifierValue" required :label="$t('common.contractId')" :hint="$t('common.contractIdHint')" />

                <VSelect name="identifierName" :label="$t('common.memberIdType')" />

                <button :disabled="!isInit || searchform.isSearchLoading" type="submit" class="btn btn-default pull-left offset-right-1">
                  {{ $t('common.btnSearch') }}
                </button>
                <VCheckbox name="allowInactive" inline>{{ $t('common.allowInactive') }}</VCheckbox>
              </VForm>
            </VTab>

            <VTab v-if="isSearchByAnyTransactionIdAllowed" label="common.transaction" name="formTransactions">
              <VTabset label="common.searchArea" buttons no-single-header>
                <VTab v-if="isSearchByPaymentTransactionIdAllowed" label="common.paymentTransactionLabel" name="paymentTransaction">
                  <VForm :form="formPaymentTransactions" @submit="onContractSearch">
                    <VInput name="paymentTransactionId" required :label="$t('common.paymentTransactionId')" :hint="$t('common.paymentTransactionIdHint')" />

                    <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default pull-left offset-right-1">
                      {{ $t('common.btnSearch') }}
                    </button>
                    <VCheckbox name="allowInactive" inline>{{ $t('common.allowInactive') }}</VCheckbox>
                  </VForm>
                </VTab>
                <VTab v-if="isSearchByRechargingTransactionIdAllowed" label="common.rechargingTransactionLabel" name="rechargingTransaction">
                  <VForm :form="formRechargingTransactions" @submit="onContractSearchByRechargingTransactions">
                    <VInput
                      name="rechargingTransactionId"
                      required
                      :label="$t('common.rechargingTransactionId')"
                      :hint="$t('common.rechargingTransactionIdHint')"
                    />

                    <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default pull-left offset-right-1">
                      {{ $t('common.btnSearch') }}
                    </button>
                    <VCheckbox name="allowInactive" inline>{{ $t('common.allowInactive') }}</VCheckbox>
                  </VForm>
                </VTab>
              </VTabset>
            </VTab>

            <VTab v-if="isSearchByUsidAllowed" label="common.subscription" name="formUsid">
              <VTabset label="common.searchArea" buttons no-single-header>
                <VTab label="common.subscriptionId" name="subscriptionId">
                  <VForm :form="formUsid" @submit="onContractSearch">
                    <VInput name="subscriptionId" required :label="$t('common.subscriptionId')" :hint="$t('common.subscriptionIdHint')" />
                    <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default pull-left offset-right-1">
                      {{ $t('common.btnSearch') }}
                    </button>
                    <VCheckbox name="allowInactive" inline>{{ $t('common.allowInactive') }}</VCheckbox>
                  </VForm>
                </VTab>
                <VTab v-if="isSearchByDescKeyAllowed" label="common.description" name="subscriptionDescription">
                  <VForm :form="formSubscriptionDescriptions" @submit="onContractSearchByDescriptions">
                    <VSelect name="descriptionKey" :label="$t('common.descriptionKey')" />
                    <VInput name="descriptionValue" required :label="$t('common.descriptionValue')" :hint="$t('common.descriptionValueHint')" />
                    <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default pull-left offset-right-1">
                      {{ $t('common.btnSearch') }}
                    </button>
                    <VCheckbox name="allowInactive" inline>{{ $t('common.allowInactive') }}</VCheckbox>
                  </VForm>
                </VTab>
              </VTabset>
            </VTab>

            <VTab v-if="isSearchByCallnumberAllowed" label="common.phone" name="formCallnumber">
              <VForm :form="formCallnumber" @submit="onContractSearch">
                <VInput name="precallnumber" required :label="$t('common.precallNo')" :hint="$t('common.precallNoHint')" />

                <VInput name="callnumber" required :label="$t('common.phone')" :hint="$t('common.phoneHint')" />

                <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default">{{ $t('common.btnSearch') }}</button>
              </VForm>
            </VTab>

            <VTab v-if="isSearchByAnyOrderIdAllowed" label="common.orderIdLabel" name="formOrderId">
              <VTabset label="common.searchArea" buttons no-single-header>
                <VTab label="common.orderIdType" name="orderIdType">
                  <VForm :form="formOrderId" @submit="onContractSearch">
                    <VInput name="orderId" required :label="$t('common.orderIdInput')" :hint="$t('common.orderIdHint')" />

                    <VSelect name="orderIdType" :label="$t('common.orderIdType')" />

                    <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default pull-left offset-right-1">
                      {{ $t('common.btnSearch') }}
                    </button>
                    <VCheckbox name="allowInactive" inline>{{ $t('common.allowInactive') }}</VCheckbox>
                  </VForm>
                </VTab>
                <VTab v-if="isSearchByDescKeyAllowed" label="common.description" name="orderDescription">
                  <VForm :form="formOrderDescriptions" @submit="onContractSearchByDescriptions">
                    <VSelect name="descriptionKey" :label="$t('common.descriptionKey')" />
                    <VInput name="descriptionValue" required :label="$t('common.descriptionValue')" :hint="$t('common.descriptionValueHint')" />
                    <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default pull-left offset-right-1">
                      {{ $t('common.btnSearch') }}
                    </button>
                    <VCheckbox name="allowInactive" inline>{{ $t('common.allowInactive') }}</VCheckbox>
                  </VForm>
                </VTab>
              </VTabset>
            </VTab>
          </div>
          <div class="col-l-7 col-m-12">
            <VSearchFormStatus v-bind="searchform" class="offset-top-1" />
          </div>
        </div>
      </VTabset>
    </section>

    <VSection id="SEARCH_RESULT_CONTRACT_SELECTION" ref="searchResultContracts" label="common.searchResults" hl-style="margin-top: 12px">
      <transition name="fade" mode="out-in" appear>
        <VContractSelection :contracts="searchResultContracts" :to="routeTo" @select="onContractSearch" />
      </transition>
    </VSection>
  </article>
</template>

<script>
import { mapGetters } from 'vuex';
import VForm from '@/components/Form/Form';
import VInput from '@/components/Form/Input';
import VSelect from '@/components/Form/Select';
import VCheckbox from '@/components/Form/Checkbox';
import {
  checkValidations,
  focusFirst,
  getFormValues,
  hasMinLength,
  initFields,
  isNumeric,
  match,
  promiseAllFields,
  isValidDescriptionValue,
  hasMaxLength,
} from '@/utils/form';
import TitleMixin from '@/mixins/title';
import Proxy from '@/proxies/ContractSearchProxy';
import VSpinner from '@/components/Spinner';
import VSection from '@/components/Section';
import SearchFormMixin from '@/mixins/searchform';
import { getErrorMessageKey } from '@/utils/i18n';
import MessageMixin from '@/mixins/message';
import { ERROR, HOME, ORDER_OVERVIEW, PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS, RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS, STOCK } from '@route-types';

export default {
  name: 'ContractSearch',

  components: {
    VSpinner,
    VTabset: () => import('@/components/UI/Tabs/Tabset'),
    VTab: () => import('@/components/UI/Tabs/Tab'),
    VSearchFormStatus: () => import('@/components/SearchFormStatus'),
    VForm,
    VInput,
    VSelect,
    VCheckbox,
    VContractSelection: () => import('./ContractSelection'),
    VSection,
    VMessage: () => import('@/components/Message'),
    VButton: () => import('@/components/Button'),
  },

  mixins: [TitleMixin, SearchFormMixin, MessageMixin],

  props: {
    errorSelectContract: {
      type: Boolean,
      required: false,
    },

    originalToName: {
      type: String,
      required: false,
      default: null,
    },

    originalToParams: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      isLoading: true,
      isCollapsed: null,
      contracts: [],
      searchResultContracts: [],
      routeTo: null,
      formContractIdentifier: null,
      formOrderId: null,
      formPaymentTransactions: initFields(
        {
          name: 'paymentTransactionId',
          validators: [hasMinLength(2), { handler: isNumeric, hint: 'common.invalidPaymentTransactionId' }],
        },
        { name: 'allowInactive' }
      ),
      formRechargingTransactions: initFields(
        {
          name: 'rechargingTransactionId',
          validators: [hasMinLength(2)],
        },
        { name: 'allowInactive' }
      ),
      formUsid: initFields({ name: 'subscriptionId', validators: hasMinLength(2) }, { name: 'allowInactive' }),
      formSubscriptionDescriptions: null,
      formOrderDescriptions: null,
      formCallnumber: initFields(
        {
          name: 'precallnumber',
          validators: [{ handler: isNumeric, hint: 'common.invalidPrecallnumber' }, hasMinLength(2)],
        },
        {
          name: 'callnumber',
          validators: [{ handler: match(/^[1-9]{1}[0-9]*$/), hint: 'common.invalidCallnumber' }, hasMinLength(3)],
        }
      ),

      msgBox: {
        type: '',
        textKey: '',
      },
    };
  },

  computed: {
    ...mapGetters('system', [
      'hasAccessPaymentTransactions',
      'hasAccessRechargeTransactions',
      'hasAccessStockOverview',
      'hasAccessOrderOverview',
      'isCrmFixednet',
      'isSearchByContractIdentifierAllowed',
      'isSearchByPaymentTransactionIdAllowed',
      'isSearchByRechargingTransactionIdAllowed',
      'isSearchByUsidAllowed',
      'isSearchByCallnumberAllowed',
      'isContractSelectionListVisible',
      'isSearchByOrderIdAllowed',
      'isSearchByExternalOrderIdAllowed',
      'isSearchByDescKeyAllowed',
      'isAuthTypeAuth',
    ]),

    isSearchByAnyTransactionIdAllowed() {
      return this.isSearchByPaymentTransactionIdAllowed || this.isSearchByRechargingTransactionIdAllowed;
    },

    isSearchByAnyOrderIdAllowed() {
      return this.isSearchByOrderIdAllowed || this.isSearchByExternalOrderIdAllowed;
    },

    isContractSelectionVisible() {
      return this.isContractSelectionListVisible && !this.isAuthTypeAuth;
    },
  },

  async created() {
    const isAnySearchFormVisible =
      this.isSearchByContractIdentifierAllowed ||
      this.isSearchByAnyTransactionIdAllowed ||
      this.isSearchByUsidAllowed ||
      this.isSearchByCallnumberAllowed ||
      this.isSearchByAnyOrderIdAllowed; // Without isSearchByDescKeyAllowed, because this depends from other rights
    if (!isAnySearchFormVisible) this.isCollapsed = null;

    const orderTypes = [];
    if (this.isSearchByOrderIdAllowed) {
      orderTypes.push({ id: 'uniqueOrderID', label: 'common.orderId' });
    }
    if (this.isSearchByExternalOrderIdAllowed) {
      orderTypes.push({ id: 'externalOrderID', label: 'common.externalId' });
    }
    this.formOrderId = initFields({ name: 'orderId', validators: hasMinLength(2) }, { name: 'orderIdType', items: orderTypes }, { name: 'allowInactive' });

    const response =
      this.isSearchByContractIdentifierAllowed ||
      (this.isSearchByUsidAllowed && this.isSearchByDescKeyAllowed) ||
      (this.isSearchByAnyOrderIdAllowed && this.isSearchByDescKeyAllowed)
        ? await new Proxy().all()
        : null;
    const convertOptions = (arr) => arr.map((item) => ({ id: item, label: item }));

    if (this.isSearchByContractIdentifierAllowed) {
      this.formContractIdentifier = initFields(
        { name: 'identifierValue', validators: hasMinLength(2), value: this.$route.params.contractId },
        { name: 'identifierName', items: convertOptions(response.identifiers), value: this.$route.params.memberIdType },
        { name: 'allowInactive' }
      );

      if (this.$route.params.memberIdType) {
        // quick login feature: skip form and press submit
        promiseAllFields(this.formContractIdentifier).then(() => {
          this.onContractSearch({
            form: this.formContractIdentifier,
            isValid: checkValidations(this.formContractIdentifier),
            values: getFormValues(this.formContractIdentifier),
          });
        });
      }
    }

    if (this.isSearchByUsidAllowed && this.isSearchByDescKeyAllowed) {
      this.formSubscriptionDescriptions = initFields(
        { name: 'descriptionKey', items: convertOptions(response.searchableDescriptionKeys) },
        { name: 'descriptionValue', validators: [hasMaxLength(4000), isValidDescriptionValue] },
        { name: 'allowInactive' }
      );
    }

    if (this.isSearchByAnyOrderIdAllowed && this.isSearchByDescKeyAllowed) {
      this.formOrderDescriptions = initFields(
        { name: 'descriptionKey', items: convertOptions(response.searchableDescriptionKeys) },
        { name: 'descriptionValue', validators: [hasMaxLength(4000), isValidDescriptionValue] },
        { name: 'allowInactive' }
      );
    }

    await this.onLoadList();

    this.isLoading = false;
  },

  methods: {
    async onLoadList() {
      this.setCompMessage(this.msgBox, 'LOADING');
      try {
        this.contracts = await new Proxy().list();
        if (this.contracts.length) {
          this.isCollapsed = false;
        }
        this.clearCompMessage(this.msgBox);
      } catch (err) {
        if (err?.data?.code === 'OPENID_NO_IDENTIFIER_FOUND') {
          this.setCompMessage(this.msgBox, 'INFO', err);
        } else if (err?.data?.code === 'BACKEND_ERROR') {
          this.setCompMessage(this.msgBox, 'ERROR', 'common.contract_search_selection_backend_error_information');
        } else {
          this.setCompMessage(this.msgBox, 'ERROR', err);
        }
      }
    },

    async onContractSearch({ values, form = null, routeTo = null, routeToParams = {} }) {
      this.searchLoading();

      try {
        await this.$store.dispatch('contract/search', values);
        this.searchSuccess();

        if (this.originalToName) {
          this.$router.push({
            name: this.originalToName,
            params: this.originalToParams,
          });
          return;
        }

        if (routeTo) {
          this.$router.push({ name: routeTo, params: routeToParams });
        } else if (form === this.formPaymentTransactions && this.hasAccessPaymentTransactions) {
          this.$router.push({
            name: PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS,
            params: { transactionId: values.paymentTransactionId, tab: 'basic' },
          });
        } else if (form === this.formRechargingTransactions && this.hasAccessRechargeTransactions) {
          this.$router.push({
            name: RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS,
            params: { transactionId: values.rechargingTransactionId },
          });
        } else if (form === this.formUsid && this.hasAccessStockOverview) {
          this.$router.push({
            name: STOCK,
            params: { uniqueSubscriptionId: values.subscriptionId },
          });
        } else if ((form === this.formOrderId || form === this.formExternalId) && this.hasAccessOrderOverview) {
          this.$router.push({
            name: ORDER_OVERVIEW,
          });
        } else {
          this.$router.push({
            name: HOME,
          });
        }
      } catch (err) {
        if (form !== null) focusFirst(form);
        this.parseError(err);
      }
    },

    async onContractSearchByDescriptions({ values, form }) {
      this.searchLoading();
      const context = form === this.formOrderDescriptions ? 'order' : 'subscription'; // See CustOrdOverviewIdentifiers.Context enum
      const routeTo = context === 'order' ? ORDER_OVERVIEW : STOCK;
      try {
        if (this.isCollapsed !== null) this.isCollapsed = true;
        const response = await new Proxy(values).searchDescriptions(context);

        if (!response.length) {
          this.searchErrorNoData();
        } else if (response.length === 1) {
          this.onContractSearch({
            values: {
              identifierName: response[0].identifierInfo.NIK0.name,
              identifierValue: response[0].identifierInfo.NIK0.value,
              allowInactive: values.allowInactive,
            },
            routeTo,
          });
          return;
        } else {
          this.searchResultContracts = response;
          this.routeTo = routeTo;
          this.$nextTick(() => this.$refs.searchResultContracts.scrollIntoView());
        }

        this.searchSuccess();
      } catch (err) {
        focusFirst(form);
        this.parseError(err);
      }
    },

    async onContractSearchByRechargingTransactions({ values, form }) {
      this.searchLoading();
      const routeTo = RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS;
      const routeToParamsOfContract = (c) => ({
        transactionIds: c.transactionIds.length > 1 ? c.transactionIds.sort().reverse().join() : null,
        transactionId: c.transactionIds[0],
      });
      try {
        if (this.isCollapsed !== null) this.isCollapsed = true;
        const response = await new Proxy(values).searchRechargingTransactions();

        if (!response.length) {
          this.searchErrorNoData();
        } else if (response.length === 1) {
          this.onContractSearch({
            values: {
              identifierName: response[0].data.identifierInfo.NIK0.name,
              identifierValue: response[0].data.identifierInfo.NIK0.value,
              allowInactive: values.allowInactive,
            },
            routeTo,
            routeToParams: routeToParamsOfContract(response[0]),
          });
          return;
        } else {
          this.searchResultContracts = response.map((c) => ({ ...c.data, routeToParams: routeToParamsOfContract(c) }));
          this.routeTo = routeTo;
          this.$nextTick(() => this.$refs.searchResultContracts.scrollIntoView());
        }

        this.searchSuccess();
      } catch (err) {
        focusFirst(form);
        this.parseError(err);
      }
    },

    onTabChange() {
      this.searchResultContracts = [];
      this.routeTo = null;
    },

    parseError(err) {
      this.isLoading = false;
      switch (err.status) {
        case 404:
          this.setErrorCode('errorcodes.NO_CONTRACT_FOUND');
          // example: form.contractId.isValid = false;
          break;
        case 400:
          this.searchErrorInvalidFormData(err?.data?.errorDetails?.errorMapping);
          break;
        default:
          // CAP-6359: Within the OpenID Flow the customer selects a contract and nothing happens with a potential substore error
          if (['SUBSTORE_ERROR', 'SUBSTORE_ERROR_FATAL'].includes(err?.data?.code)) {
            this.$router.push({ name: ERROR, params: { error: err } });
          }
          this.setErrorCode(getErrorMessageKey(err));
      }
    },
  },
};
</script>
